import { useEffect, useMemo } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Phone, Mail, ChevronRight, Sparkles, ShieldCheck,
  CheckCircle2, MapPin, TrendingUp, MessageCircle, Building2, Home,
} from "lucide-react";
import { BRAND } from "@/config/brand";
import { INDUSTRIES, INDUSTRY_FAQS, getIndustryBySlug } from "@/config/industries";
import { DFW_CITIES } from "@/config/cities";
import { getIndustryCityOverride, isPriorityIndustryCity } from "@/config/industryCityOverrides";
import PlugitAIChat from "@/pages/PlugitAIChat";

const C = BRAND.colors;

function upsertMeta(name, content, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function upsertCanonical(href) {
  let el = document.querySelector("link[rel='canonical']");
  if (!el) { el = document.createElement("link"); el.rel = "canonical"; document.head.appendChild(el); }
  el.href = href;
}
function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
}

// Format $ figures
const fmt = (n) => `$${(n ?? 0).toLocaleString()}`;

export default function IndustryCityPage() {
  const { industry: iSlug, city: cSlug } = useParams();
  const ind = useMemo(() => getIndustryBySlug(iSlug), [iSlug]);
  const city = useMemo(() => DFW_CITIES.find((c) => c.slug === cSlug), [cSlug]);

  // Priority-recovery gate (Aug 2026): only 50 hand-rewritten combos stay
  // indexable — the remaining 482 template combos ship `noindex,follow`
  // until each is rewritten with genuinely unique content.
  const isPriority = useMemo(() => isPriorityIndustryCity(iSlug, cSlug), [iSlug, cSlug]);
  const override = useMemo(() => getIndustryCityOverride(iSlug, cSlug), [iSlug, cSlug]);

  // Nearby cities that also have combo pages
  const nearbyCombos = useMemo(() => {
    if (!city || !ind) return [];
    return (city.nearby || [])
      .map((slug) => DFW_CITIES.find((c) => c.slug === slug))
      .filter(Boolean)
      .slice(0, 6);
  }, [city, ind]);

  // Sibling industries in the same city
  const siblingIndustries = useMemo(() => {
    if (!ind) return [];
    return INDUSTRIES.filter((i) => i.slug !== ind.slug);
  }, [ind]);

  useEffect(() => {
    if (!ind || !city) return;
    const origin = process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "https://plugitai.com");
    const url = `${origin}/for/${ind.slug}/${city.slug}`;
    const title = `AI Websites & Sales Agents for ${ind.display} in ${city.name}, TX | PlugitAI`;
    const desc = `AI-powered websites and 24/7 sales agents built for ${ind.audience} in ${city.name}, ${city.county}. ${ind.tagline} Lock in your project with $299.99.`;
    document.title = title;
    upsertMeta("description", desc);
    // Priority-recovery gate: keep 50 hand-rewritten combos indexable, put
    // the rest on `noindex,follow` so Google stops flagging them as thin.
    // The pages still render for users and internal links still flow.
    upsertMeta("robots", isPriority
      ? "index,follow,max-image-preview:large,max-snippet:-1"
      : "noindex,follow");
    upsertMeta("og:title", title, true);
    upsertMeta("og:description", desc, true);
    upsertMeta("og:type", "website", true);
    upsertMeta("og:url", url, true);
    upsertMeta("og:image", `${origin}/hero-ai-twin.jpg`, true);
    upsertMeta("twitter:card", "summary_large_image");
    upsertCanonical(url);

    // LocalBusiness — geo-tied to the specific city
    upsertJsonLd("ld-combo-localbusiness", {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: `PlugitAI · ${ind.display} in ${city.name}, TX`,
      url,
      description: desc,
      telephone: "+12148155966",
      email: "cgarciathanksyou@gmail.com",
      priceRange: "$299.99–$1800",
      areaServed: {
        "@type": "City",
        name: `${city.name}, TX`,
        containedInPlace: { "@type": "AdministrativeArea", name: city.county },
      },
      address: {
        "@type": "PostalAddress",
        addressLocality: city.name,
        addressRegion: "TX",
        postalCode: city.zip,
        addressCountry: "US",
      },
      geo: { "@type": "GeoCoordinates", latitude: city.lat, longitude: city.lng },
      serviceType: `AI website and AI sales agent for ${ind.display}`,
      makesOffer: {
        "@type": "Offer",
        priceCurrency: "USD",
        price: "299.99",
        description: "$299.99 project lock-in. Full builds typically under $1,800.",
      },
    });
    upsertJsonLd("ld-combo-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin },
        { "@type": "ListItem", position: 2, name: "Industries", item: `${origin}/for` },
        { "@type": "ListItem", position: 3, name: ind.display, item: `${origin}/for/${ind.slug}` },
        { "@type": "ListItem", position: 4, name: `${city.name}, TX`, item: url },
      ],
    });
    upsertJsonLd("ld-combo-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: INDUSTRY_FAQS(ind).map((f) => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });

    return () => {
      ["ld-combo-localbusiness", "ld-combo-breadcrumb", "ld-combo-faq"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [ind, city]);

  if (!ind) return <Navigate to="/for" replace />;
  if (!city) return <Navigate to={`/for/${ind.slug}`} replace />;

  const faqs = INDUSTRY_FAQS(ind);
  const accent = ind.accent;
  const accentSoft = ind.accentSoft;

  // Data-driven ROI display — supports weeklyLoss / weeklyLift / dailyLoss schemas.
  const roi = ind.roi || {};
  const isLift = roi.weeklyLift != null;
  const roiValue = roi.weeklyLift ?? roi.weeklyLoss ?? roi.dailyLoss ?? 0;
  const roiFigure = fmt(roiValue);
  const roiCadence = roi.dailyLoss != null && roi.weeklyLoss == null && roi.weeklyLift == null
    ? "/ day"
    : isLift ? "/ week potential" : "/ week";

  return (
    <div className="min-h-screen w-full overflow-x-hidden"
         style={{ background: C.ink, color: C.text, fontFamily: "'Inter', system-ui, sans-serif" }}
         data-testid={`industry-city-page-${ind.slug}-${city.slug}`}>
      <TopNav />

      <div aria-hidden className="pointer-events-none fixed inset-0 z-0"
           style={{
             background: `radial-gradient(1200px 700px at 15% -10%, ${accent}22, transparent 60%),
                          radial-gradient(900px 600px at 90% 20%, ${accent}18, transparent 60%),
                          radial-gradient(1400px 900px at 60% 110%, ${C.blueDeep}44, transparent 70%)`,
           }} />

      {/* Hero */}
      <section className="relative z-10 pt-28 md:pt-36 pb-16 md:pb-24">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <Breadcrumbs ind={ind} city={city} />

          <div className="mt-6 mb-8 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-[0.2em]"
               style={{
                 background: `linear-gradient(135deg, ${accent} 0%, ${accentSoft} 100%)`,
                 color: "white",
                 boxShadow: `0 12px 32px -10px ${accent}88`,
               }}>
            <Sparkles size={12} />
            AI for {ind.name} · {city.name}, TX
          </div>

          <h1 className="font-black tracking-[-0.02em] leading-[0.98]"
              style={{ fontSize: "clamp(2.2rem, 5vw, 4.8rem)", color: "white" }}
              data-testid="combo-h1">
            AI websites &amp; sales agents for{" "}
            <span style={{
              backgroundImage: `linear-gradient(135deg, ${accent} 0%, ${accentSoft} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              filter: `drop-shadow(0 0 24px ${accent}66)`,
            }}>
              {ind.display}
            </span>
            {" "}in {city.name}, TX.
          </h1>

          <p className="mt-6 max-w-[820px] text-lg md:text-xl leading-relaxed" style={{ color: C.textDim }}>
            {ind.tagline}
          </p>

          {/* Unique local paragraph — priority combos get hand-crafted content;
              non-priority combos fall back to the template one-liner (which is
              fine because they're noindex,follow). */}
          {override ? (
            <p className="mt-4 max-w-[820px] text-base md:text-lg leading-relaxed"
               style={{ color: C.textDim }}
               data-testid="combo-priority-intro">
              {override.intro}
            </p>
          ) : (
            <p className="mt-4 max-w-[820px] text-base md:text-lg leading-relaxed" style={{ color: C.textDim }}>
              {city.hook} That&apos;s exactly why {ind.audience.split(",")[0].trim()} in {city.name} lose so much revenue to unanswered calls and slow intake — and exactly what a PlugitAI twin fixes on day one.
            </p>
          )}

          {/* Local specs strip */}
          <div className="mt-8 flex flex-wrap gap-2 max-w-[820px]" data-testid="combo-city-specs">
            <SpecChip icon={<MapPin size={12} />} label={`${city.name}, TX ${city.zip}`} color={accent} />
            <SpecChip icon={<Building2 size={12} />} label={city.county} color={accent} />
            <SpecChip icon={<Home size={12} />} label={`Pop ${city.population}`} color={accent} />
            <SpecChip icon={<Sparkles size={12} />} label={ind.display} color={accent} />
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a href="tel:+12148155966"
               className="inline-flex items-center gap-2 px-7 h-14 rounded-full font-semibold text-base transition-all hover:-translate-y-0.5"
               style={{
                 background: `linear-gradient(135deg, ${accent} 0%, ${accentSoft} 100%)`,
                 color: "white",
                 boxShadow: `0 24px 60px -20px ${accent}aa`,
               }}
               data-testid="combo-cta-call">
              <Phone size={14} /> Talk to Kit — (214) 815-5966 <ArrowRight size={16} />
            </a>
            <button type="button"
                    onClick={() => document.querySelector("[data-testid=plugitai-chat-open]")?.click()}
                    className="inline-flex items-center gap-2 px-6 h-14 rounded-full font-semibold text-base border transition-all hover:bg-white/5"
                    style={{ borderColor: `${accent}77`, color: "white" }}
                    data-testid="combo-cta-chat">
              <MessageCircle size={14} /> Chat with the AI now <ArrowUpRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Local ROI banner — combines industry math + city character */}
      <section className="relative z-10 py-16 md:py-24 border-y"
               style={{ borderColor: C.line, background: "rgba(11,21,36,0.72)" }}
               data-testid="combo-local-roi">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-[minmax(0,1.4fr),minmax(0,1fr)] gap-8 md:gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accentSoft }}>
              What {city.name} {ind.name.toLowerCase()} are leaving on the table
            </div>
            <div className="flex items-baseline gap-3 mb-4">
              <div className="font-black" style={{ fontSize: "clamp(2.6rem, 6vw, 5rem)", color: accent, lineHeight: 1 }}>
                {roiFigure}
              </div>
              <div className="text-sm md:text-base font-bold" style={{ color: C.textDim }}>
                {roiCadence}
              </div>
            </div>
            <p className="max-w-[600px] text-sm md:text-base leading-relaxed" style={{ color: C.textDim }}>
              {ind.roi.note} A {city.name} shop with even a mid-sized customer base sees this leak every single week without an AI twin catching after-hours calls, DMs, and inbound forms.
            </p>
          </div>

          <div className="rounded-2xl p-6 border"
               style={{
                 borderColor: `${accent}55`,
                 background: `linear-gradient(135deg, ${accent}12 0%, transparent 100%), rgba(17,24,39,0.55)`,
                 boxShadow: `0 40px 100px -40px ${accent}55`,
               }}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                 style={{ background: `${accent}22`, border: `1px solid ${accent}55` }}>
              <TrendingUp size={18} style={{ color: accent }} />
            </div>
            <div className="font-black text-white text-lg md:text-xl mb-2 leading-tight">
              Lock in your {city.name} build for{" "}
              <span style={{ color: accentSoft }}>$299.99</span>.
            </div>
            <p className="text-sm md:text-base leading-relaxed" style={{ color: C.textDim }}>
              Full builds under $1,800. Most {city.name} {ind.name.toLowerCase()} pay it back inside the first month.
            </p>
          </div>
        </div>
      </section>

      {/* What the AI twin does */}
      <section className="relative z-10 py-24 md:py-32" data-testid="combo-twin-does">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accentSoft }}>
            What your {city.name} {ind.name.toLowerCase()} AI twin does every day
          </div>
          <h2 className="font-black tracking-[-0.02em] leading-[1.02] mb-12"
              style={{ fontSize: "clamp(1.9rem, 3.6vw, 3rem)", color: "white" }}>
            8 jobs. Zero payroll.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {ind.twinDoes.map((line, i) => (
              <div key={i}
                   className="rounded-2xl p-4 md:p-5 border flex items-start gap-3"
                   style={{
                     borderColor: C.line,
                     background: "rgba(17,24,39,0.55)",
                     backdropFilter: "blur(10px)",
                   }}
                   data-testid={`combo-twin-does-${i}`}>
                <CheckCircle2 size={18} className="shrink-0 mt-0.5" style={{ color: accent }} />
                <div className="text-sm md:text-base font-semibold text-white leading-snug">{line}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* City context — what makes {city} unique */}
      <section className="relative z-10 py-20 md:py-28 border-y"
               style={{ borderColor: C.line, background: "rgba(11,21,36,0.72)" }}
               data-testid="combo-city-context">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accentSoft }}>
            Why {city.name} is different
          </div>
          <h3 className="font-black text-white mb-5" style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)" }}>
            {city.name}, {city.county} — Population {city.population}.
          </h3>
          <p className="text-base md:text-lg leading-relaxed" style={{ color: C.textDim }}>
            {city.hook}
          </p>
          {/* Priority-combo unique local block — genuinely unique per city+industry pair */}
          {override && (
            <div className="mt-6 rounded-2xl p-5 border" style={{ borderColor: `${accent}44`, background: `${accent}0c` }} data-testid="combo-priority-local-block">
              <div className="text-[10px] uppercase tracking-[0.22em] font-black mb-3" style={{ color: accentSoft }}>
                Local angle · {city.name} {ind.name.toLowerCase()}
              </div>
              <p className="text-sm md:text-base leading-relaxed mb-4" style={{ color: C.textDim }}>
                {override.localAngle}
              </p>
              <div className="text-[10px] uppercase tracking-[0.22em] font-black mb-2" style={{ color: accentSoft }}>
                Recent example
              </div>
              <p className="text-sm md:text-base leading-relaxed" style={{ color: C.textDim }}>
                {override.example}
              </p>
            </div>
          )}
          <div className="mt-6 flex flex-wrap gap-2">
            {(city.industries || []).map((biz) => (
              <span key={biz}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
                    style={{
                      background: `${accent}12`,
                      borderColor: `${accent}55`,
                      color: accentSoft,
                    }}>
                <Sparkles size={12} /> {biz}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Guardrails */}
      <section className="relative z-10 py-16 md:py-20" data-testid="combo-guardrails">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-3 flex items-center gap-2" style={{ color: accentSoft }}>
            <ShieldCheck size={12} /> Approved-only rails
          </div>
          <h3 className="font-black text-white mb-6" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2rem)" }}>
            The AI never freelances — even in {city.name}.
          </h3>
          <ul className="space-y-3">
            {ind.guardrails.map((g, i) => (
              <li key={i} className="rounded-xl p-4 border flex items-start gap-3"
                  style={{ borderColor: `${accent}33`, background: "rgba(17,24,39,0.55)" }}>
                <ShieldCheck size={16} className="shrink-0 mt-0.5" style={{ color: accent }} />
                <span className="text-sm md:text-base text-white/90 leading-snug">{g}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Nearby cities cross-links */}
      {nearbyCombos.length > 0 && (
        <section className="relative z-10 py-16 md:py-24 border-t" style={{ borderColor: C.line }} data-testid="combo-nearby">
          <div className="mx-auto max-w-[1200px] px-6 md:px-10">
            <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accentSoft }}>
              We also build for {ind.name.toLowerCase()} in nearby cities
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {nearbyCombos.map((nc) => (
                <Link key={nc.slug} to={`/for/${ind.slug}/${nc.slug}`}
                      className="rounded-xl p-3 border transition-all hover:-translate-y-1"
                      style={{ borderColor: `${accent}33`, background: "rgba(17,24,39,0.55)" }}
                      data-testid={`combo-nearby-${nc.slug}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <MapPin size={11} style={{ color: accent }} />
                    <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: C.textFade }}>
                      {nc.county}
                    </div>
                  </div>
                  <div className="font-black text-white text-sm md:text-base leading-tight">
                    {nc.name}
                  </div>
                  <div className="text-[10px] mt-1" style={{ color: accentSoft }}>
                    AI for {ind.name} →
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Same city — other industries */}
      <section className="relative z-10 py-16 md:py-20 border-t"
               style={{ borderColor: C.line, background: "rgba(11,21,36,0.72)" }}
               data-testid="combo-sibling-industries">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accentSoft }}>
            Other {city.name} industries we build for
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {siblingIndustries.map((si) => (
              <Link key={si.slug} to={`/for/${si.slug}/${city.slug}`}
                    className="group rounded-2xl p-4 md:p-5 border transition-all hover:-translate-y-1"
                    style={{
                      borderColor: `${si.accent}44`,
                      background: `linear-gradient(160deg, ${si.accent}14 0%, transparent 55%), rgba(17,24,39,0.55)`,
                    }}
                    data-testid={`combo-sibling-${si.slug}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
                     style={{
                       background: `linear-gradient(135deg, ${si.accent} 0%, ${si.accentSoft} 100%)`,
                     }}>
                  <Sparkles size={14} className="text-white" />
                </div>
                <div className="font-black text-white text-base md:text-lg leading-tight mb-1">
                  AI for {si.name}
                </div>
                <div className="text-xs" style={{ color: C.textDim }}>
                  in {city.name}, TX
                </div>
                <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold" style={{ color: si.accentSoft }}>
                  Explore → <ArrowUpRight size={11} />
                </div>
              </Link>
            ))}
            <Link to={`/tx/${city.slug}`}
                  className="rounded-2xl p-4 md:p-5 border transition-all hover:-translate-y-1"
                  style={{ borderColor: C.line, background: "rgba(17,24,39,0.55)" }}
                  data-testid="combo-back-to-city">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
                   style={{ background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)` }}>
                <MapPin size={14} className="text-white" />
              </div>
              <div className="font-black text-white text-base md:text-lg leading-tight mb-1">
                {city.name} full overview
              </div>
              <div className="text-xs" style={{ color: C.textDim }}>
                All PlugitAI in {city.name}
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-white">
                Explore → <ArrowUpRight size={11} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 py-24 md:py-32" data-testid="combo-faq">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accentSoft }}>
            Questions from {ind.display} in {city.name}
          </div>
          <h2 className="font-black tracking-[-0.02em] leading-[1.02] mb-10"
              style={{ fontSize: "clamp(1.9rem, 3.6vw, 3rem)", color: "white" }}>
            FAQ
          </h2>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <details key={i}
                       className="group rounded-2xl p-5 md:p-6 border"
                       style={{ borderColor: C.line, background: "rgba(17,24,39,0.55)", backdropFilter: "blur(10px)" }}
                       data-testid={`combo-faq-${i}`}>
                <summary className="flex items-center justify-between gap-4 cursor-pointer text-base md:text-lg font-bold text-white list-none">
                  <span>{f.q}</span>
                  <ChevronRight size={18} className="shrink-0 transition-transform group-open:rotate-90" style={{ color: accentSoft }} />
                </summary>
                <p className="mt-3 text-sm md:text-base leading-relaxed" style={{ color: C.textDim }}>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 pb-24 md:pb-36">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10">
          <div className="relative rounded-3xl p-8 md:p-14 border overflow-hidden"
               style={{
                 background: `linear-gradient(135deg, ${accent}22 0%, ${accentSoft}12 100%), ${C.inkSoft}`,
                 borderColor: `${accent}66`,
                 boxShadow: `0 80px 200px -60px ${accent}66`,
               }}
               data-testid="combo-final-cta">
            <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: accentSoft }}>
              Lock in your build
            </div>
            <h3 className="font-black tracking-[-0.02em] leading-[1.02]"
                style={{ fontSize: "clamp(1.8rem, 3.6vw, 2.8rem)", color: "white" }}>
              Ready to give your {city.name} {ind.name.toLowerCase()} an AI twin?
            </h3>
            <p className="mt-4 max-w-[640px] text-base md:text-lg leading-relaxed text-white/85">
              Lock your project in with{" "}
              <span className="font-black" style={{ color: accentSoft }}>$299.99</span>{" "}
              and Kit will personally design your website, the AI twin, and the sales system around your {city.name} {ind.name.toLowerCase()}. Not a template. Real work by real people.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href="tel:+12148155966"
                 className="inline-flex items-center gap-2 px-6 h-14 rounded-full font-semibold text-base"
                 style={{
                   background: `linear-gradient(135deg, ${accent} 0%, ${accentSoft} 100%)`,
                   color: "white",
                   boxShadow: `0 24px 60px -20px ${accent}aa`,
                 }}>
                <Phone size={14} /> Call (214) 815-5966 <ArrowRight size={16} />
              </a>
              <a href={`mailto:cgarciathanksyou@gmail.com?subject=${encodeURIComponent(`AI build for ${ind.display} in ${city.name}, TX`)}`}
                 className="inline-flex items-center gap-2 px-6 h-14 rounded-full font-semibold text-base border"
                 style={{ borderColor: `${accent}66`, color: "white" }}>
                <Mail size={14} /> Email me
              </a>
              <Link to={`/for/${ind.slug}`}
                    className="inline-flex items-center gap-2 px-4 h-11 rounded-full font-semibold text-sm border"
                    style={{ borderColor: C.line, color: C.textDim }}>
                Back to {ind.display} <ArrowUpRight size={13} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ComboFooter ind={ind} city={city} />
      <PlugitAIChat />
    </div>
  );
}

// ─────────────── helpers ───────────────

function SpecChip({ icon, label, color }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
          style={{
            background: `${color}12`,
            borderColor: `${color}55`,
            color: "white",
          }}>
      {icon} {label}
    </span>
  );
}

function Breadcrumbs({ ind, city }) {
  return (
    <nav className="text-xs uppercase tracking-[0.18em] flex items-center gap-2 flex-wrap" style={{ color: C.textFade }}>
      <Link to="/" className="hover:text-white transition-colors">Home</Link>
      <ChevronRight size={12} />
      <Link to="/for" className="hover:text-white transition-colors">Industries</Link>
      <ChevronRight size={12} />
      <Link to={`/for/${ind.slug}`} className="hover:text-white transition-colors">{ind.display}</Link>
      <ChevronRight size={12} />
      <span style={{ color: C.text }}>{city.name}, TX</span>
    </nav>
  );
}

function TopNav() {
  return (
    <header className="fixed top-0 left-0 right-0 z-50"
            style={{ background: "rgba(10,15,30,0.72)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${C.line}` }}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={BRAND.logos.plugitai} alt="PlugitAI" className="h-10 md:h-12 w-auto" />
          <span className="hidden xl:inline-block whitespace-nowrap text-[10px] uppercase tracking-[0.22em] pl-3 border-l gold-shine gold-shine-dark" style={{ borderColor: C.line }}>
            Powered by {BRAND.engineName}
          </span>
        </Link>
        <a href="tel:+12148155966"
           className="inline-flex items-center gap-2 px-4 md:px-5 h-10 md:h-11 rounded-full font-semibold text-sm"
           style={{ background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`, color: "white" }}>
          <Phone size={14} /> (214) 815-5966
        </a>
      </div>
    </header>
  );
}

function ComboFooter({ ind, city }) {
  return (
    <footer className="relative z-10 border-t" style={{ borderColor: C.line, background: "rgba(10,15,30,0.85)" }}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-12 grid grid-cols-1 md:grid-cols-3 gap-10">
        <div>
          <img src={BRAND.logos.plugitai} alt="PlugitAI" className="h-11 w-auto mb-4" />
          <p className="text-sm max-w-[420px]" style={{ color: C.textDim }}>
            AI websites and sales agents for {ind.audience} in {city.name}, {city.county}. Built and personally designed by Kit.{" "}
            <span className="gold-shine gold-shine-dark uppercase tracking-[0.22em] text-[10px]">Powered by {BRAND.engineName}</span>.
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] mb-4" style={{ color: C.textFade }}>Nearby cities</div>
          <ul className="space-y-2 text-sm">
            {(city.nearby || []).slice(0, 6).map((slug) => {
              const c = DFW_CITIES.find((x) => x.slug === slug);
              if (!c) return null;
              return (
                <li key={slug}>
                  <Link to={`/for/${ind.slug}/${c.slug}`} className="hover:text-white" style={{ color: C.textDim }}>
                    AI for {ind.name} in {c.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] mb-4" style={{ color: C.textFade }}>Contact</div>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2" style={{ color: C.textDim }}>
              <Phone size={12} /> <a href="tel:+12148155966" className="hover:text-white">(214) 815-5966</a>
            </li>
            <li className="flex items-center gap-2" style={{ color: C.textDim }}>
              <Mail size={12} /> <a href="mailto:cgarciathanksyou@gmail.com" className="hover:text-white">Email Me</a>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
}
